'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useLayoutEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { LoadingPageTemplate } from '@/components/client';
import { ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useLogout } from '@/hooks/useLogout';
import { useAuthStore } from '@/stores/AuthStore';

interface GuestAccessControllerProps {
  children: React.ReactNode;
}

const GuestAccessController = ({ children }: GuestAccessControllerProps) => {
  const [isLogin, isDisabledAccessControl] = useAuthStore(
    useShallow((state) => [state.isLogin, state.isDisabledAccessControl]),
  );

  const { replace } = useCustomRouter();

  const pathname = usePathname();
  const searchParams = useSearchParams();

  const { logout } = useLogout();

  useLayoutEffect(() => {
    if (isDisabledAccessControl) return;

    if (!searchParams || !pathname) return;

    if (searchParams.get('logout') === 'true') {
      const url = new URL(location.href);

      url.searchParams.delete('logout');

      logout({ redirect: url.href });

      return;
    }

    if (isLogin) {
      replace(ROUTES.HOME.pathname);

      return;
    }
  }, [
    isDisabledAccessControl,
    isLogin,
    logout,
    pathname,
    replace,
    searchParams,
  ]);

  //* bfcache 복원 시에는 effect가 재실행되지 않아 인증 가드가 스테일해진다(이미 로그인했는데 로그인
  //* 화면이 그대로 남는 문제). 복원 시 reload해 AuthProvider가 쿠키를 재판독하고 가드가 다시 돌게 한다.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        window.location.reload();
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  return isDisabledAccessControl || isLogin === false ? (
    children
  ) : (
    <LoadingPageTemplate unmountedPlaceholder />
  );
};

export default GuestAccessController;
